import { Button } from '@/components/ui/button';
import { HelpTypePreview } from '@/nodes/HelpTypePreview';
import {
    EquipmentTypeDisplay,
    InfoTypeDisplay,
    NodeBody,
    NodeContainer,
    NodeErrorIcon,
    NodeHeader,
    NodePrompts,
} from '@/nodes/NodeUI';
import type { GuideNodeId } from '@rock/types';
import { outgoingHandles } from '@rock/types';
import { Handle, type NodeProps, Position } from '@xyflow/react';
import { type FC, useContext } from 'react';
import { FlowParamsContext } from '../app/FlowInitializer';
import type { SelectNodeType } from './types';

const SelectNode: FC<NodeProps<SelectNodeType>> = ({ id, selected, data }) => {
    const paths = outgoingHandles.select(id as GuideNodeId, data);
    const { spaceId } = useContext(FlowParamsContext);

    return (
        <NodeContainer>
            <NodeErrorIcon id={id} />
            <NodeHeader id={id} type={data.type} title={data.pageTitle} />
            <NodeBody selected={selected}>
                <div className="relative">
                    <NodePrompts prompts={data.prompts} />
                    <Handle type="target" position={Position.Left} />
                    {paths.type === 'multi' && <Handle type="source" id={paths.next} position={Position.Right} />}
                </div>

                {data.infoTypeId && (
                    <div className="flex flex-col items-center">
                        <InfoTypeDisplay spaceId={spaceId} typeId={data.infoTypeId} />
                    </div>
                )}

                <div className="flex flex-col items-end px-3">
                    <HelpTypePreview
                        helpType={data.helpType}
                        spaceId={spaceId}
                        modelHelpTypeId={data.modelHelpEquipmentTypeId}
                    />
                </div>

                <div className="py-2">
                    {data.sections.map((section, sectionIndex) => {
                        return (
                            <div key={sectionIndex}>
                                {section.heading && <h6 className="text-xs p-1 italic text-left">{section.heading}</h6>}
                                {section.options.map((option, optionIndex) => (
                                    <div
                                        key={optionIndex}
                                        className={`border-t-1 border-gray-300 py-2 bg-white text-xs relative`}
                                    >
                                        <div className="flex flex-row items-center justify-center gap-3">
                                            {option.title}
                                            {option.equipmentTypeId && (
                                                <EquipmentTypeDisplay
                                                    spaceId={spaceId}
                                                    typeId={option.equipmentTypeId}
                                                />
                                            )}
                                            {data.multi?.enabled && option.defaultSelected && (
                                                <span title="Selected by default">✅</span>
                                            )}
                                        </div>
                                        {option.subtitle && (
                                            <div className="text-xs text-gray-500 italic">{option.subtitle}</div>
                                        )}
                                        {paths.type === 'single' && (
                                            <Handle
                                                type="source"
                                                id={paths.sectionOptions[sectionIndex]?.[optionIndex]}
                                                position={Position.Right}
                                            />
                                        )}
                                    </div>
                                ))}
                                {section.options.length === 0 && (
                                    <div className={`border-t-1 border-gray-300 py-1 bg-white text-xs relative`}>
                                        ---
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    {data.sections.length === 0 && (
                        <div className={`border-t-1 border-gray-300 py-1 bg-white text-xs relative`}>---</div>
                    )}
                    <div className={`border-t-1 border-gray-300`} />
                </div>
                {data.multi?.enabled && (
                    <div className="pb-2">
                        <Button size="sm">{data.multi.buttonLabel || 'Button'}</Button>
                    </div>
                )}
            </NodeBody>
        </NodeContainer>
    );
};

export default SelectNode;
